'use client'

import { twConfig } from '@/lib/tailwind/twConfig'
import { useMemo } from 'react'
import { useWindowSize } from 'usehooks-ts'

export type Breakpoint = 'mobile' | 'tablet' | 'desktop'

/**
 * Hook to determine the current viewport size category based on Tailwind breakpoints
 * @param options Optional configuration options
 * @returns Object with boolean values for each breakpoint category
 */
export function useBreakpoints(options?: { debounceDelay?: number }) {
  const { width } = useWindowSize({
    debounceDelay: options?.debounceDelay ?? 150,
  })

  // Parse breakpoint values from the Tailwind config
  const tabletWidth = Number.parseInt(twConfig.theme.screens.tablet satisfies `${number}px`, 10)
  const desktopWidth = Number.parseInt(twConfig.theme.screens.desktop satisfies `${number}px`, 10)

  // Determine which breakpoint we're currently at
  const isMobileWidth = width < tabletWidth
  const isTabletWidth = width >= tabletWidth && width < desktopWidth
  const isDesktopWidth = width >= desktopWidth

  const breakpoint: Breakpoint = isMobileWidth
    ? 'mobile'
    : isTabletWidth
      ? 'tablet'
      : isDesktopWidth
        ? 'desktop'
        : 'mobile'

  return {
    breakpoint,
    isMobileWidth,
    isTabletWidth,
    isDesktopWidth,
    // Return the current width for convenience
    width,
  }
}

export type ResponsiveProp<T extends string | boolean | number> =
  | T
  | { mobile: T; tablet: T; desktop: T }

/**
 * @description
 * Useful when we want to mask tailwind classes implementation
 * AND IF the result varies with the current breakpoint.
 *
 * @example
 * ```tsx
 * /// With a simple boolean responsive prop value
 *
 * function MyComponent({ isRound }: { isRound: ResponsiveValue<boolean> }) {
 *   const isRoundValue = useResponsiveProp(isRound)
 *   return (
 *     <div className={isRoundValue ? 'rounded-full' : 'rounded-md'} />
 *   )
 * }
 *
 * <MyComponent isRound={{ mobile: true, tablet: true, desktop: true }} />
 * // is equivalent to:
 * <MyComponent isRound />
 * ```
 */
export function useResponsiveProp<T extends string | boolean | number>(
  value: ResponsiveProp<T>,
): T {
  const { breakpoint } = useBreakpoints()

  return useMemo(() => {
    if (typeof value !== 'object') {
      return value
    }

    switch (breakpoint) {
      case 'desktop':
        return value.desktop ?? value.tablet ?? value.mobile
      case 'tablet':
        return value.tablet ?? value.mobile
      default:
        return value.mobile
    }
  }, [breakpoint, value])
}
